import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin, Phone, Mail, Clock } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
    "Full Home Renovation",
    "Living Room",
    "Bedroom",
    "Modular Kitchen",
    "Bathroom",
    "Luxury Villa",
    "Commercial / Office",
    "Other",
];

const budgetRanges = [
    "Below ₹10 Lakh",
    "₹10 – ₹25 Lakh",
    "₹25 – ₹50 Lakh",
    "₹50 Lakh – ₹1 Crore",
    "₹1 Crore +",
];

const initialForm = {
    name: "",
    phone: "",
    email: "",
    city: "Hyderabad",
    project_type: "",
    budget_range: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (key) => (e) => {
        const value = typeof e === "string" ? e : e.target.value;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const validate = () => {
        if (form.name.trim().length < 2) return "Please enter your full name.";
        if (form.phone.trim().length < 6) return "Please enter a valid phone number.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
        if (!form.city.trim()) return "Please enter your city.";
        if (!form.project_type) return "Please select a project type.";
        if (!form.budget_range) return "Please select a budget range.";
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/consultations`, form);
            toast.success("Thank you. A design manager will reach out within 24 hours.");
            setForm(initialForm);
        } catch (e2) {
            console.error(e2);
            toast.error("Something went wrong. Please try again or WhatsApp us.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="bg-charcoal text-cream">
            <section className="container-pad mx-auto pt-40 pb-20 md:pt-48 md:pb-24 border-b border-[#262626]">
                <Reveal>
                    <p className="overline mb-8">— Contact</p>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tighter text-cream max-w-5xl">
                        Begin a <em className="italic text-gold">conversation.</em>
                    </h1>
                    <p className="mt-8 max-w-xl text-base md:text-lg text-[#A3A3A3]">
                        Tell us about your space. We'll respond within 24 hours with the next steps —
                        and a complimentary design consultation slot.
                    </p>
                </Reveal>
            </section>

            <section className="container-pad mx-auto py-20 md:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* FORM */}
                    <Reveal className="lg:col-span-7">
                        <form
                            data-testid="consultation-form"
                            onSubmit={onSubmit}
                            className="space-y-8"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="field-label" htmlFor="name">Full Name</label>
                                    <Input
                                        id="name"
                                        data-testid="form-name"
                                        value={form.name}
                                        onChange={handleChange("name")}
                                        placeholder="Aarav Reddy"
                                        className="bg-transparent border-0 border-b border-[#262626] rounded-none px-0 py-3 h-auto text-cream placeholder:text-[#5a5a55] focus-visible:ring-0 focus-visible:border-gold text-base"
                                    />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="phone">Phone</label>
                                    <Input
                                        id="phone"
                                        data-testid="form-phone"
                                        value={form.phone}
                                        onChange={handleChange("phone")}
                                        placeholder="+91 98765 43210"
                                        className="bg-transparent border-0 border-b border-[#262626] rounded-none px-0 py-3 h-auto text-cream placeholder:text-[#5a5a55] focus-visible:ring-0 focus-visible:border-gold text-base"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="field-label" htmlFor="email">Email</label>
                                    <Input
                                        id="email"
                                        type="email"
                                        data-testid="form-email"
                                        value={form.email}
                                        onChange={handleChange("email")}
                                        placeholder="aarav@example.com"
                                        className="bg-transparent border-0 border-b border-[#262626] rounded-none px-0 py-3 h-auto text-cream placeholder:text-[#5a5a55] focus-visible:ring-0 focus-visible:border-gold text-base"
                                    />
                                </div>
                                <div>
                                    <label className="field-label" htmlFor="city">City</label>
                                    <Input
                                        id="city"
                                        data-testid="form-city"
                                        value={form.city}
                                        onChange={handleChange("city")}
                                        placeholder="Hyderabad"
                                        className="bg-transparent border-0 border-b border-[#262626] rounded-none px-0 py-3 h-auto text-cream placeholder:text-[#5a5a55] focus-visible:ring-0 focus-visible:border-gold text-base"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="field-label">Project Type</label>
                                    <Select value={form.project_type} onValueChange={(v) => handleChange("project_type")(v)}>
                                        <SelectTrigger
                                            data-testid="form-project-type"
                                            className="bg-transparent border-0 border-b border-[#262626] rounded-none px-0 py-3 h-auto text-cream focus:ring-0 focus:border-gold text-base [&>span]:text-cream data-[placeholder]:text-[#5a5a55]"
                                        >
                                            <SelectValue placeholder="Select a project type" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-charcoal-2 border-[#262626] text-cream">
                                            {projectTypes.map((p) => (
                                                <SelectItem key={p} value={p} className="focus:bg-gold/15 focus:text-gold">{p}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="field-label">Budget Range</label>
                                    <Select value={form.budget_range} onValueChange={(v) => handleChange("budget_range")(v)}>
                                        <SelectTrigger
                                            data-testid="form-budget"
                                            className="bg-transparent border-0 border-b border-[#262626] rounded-none px-0 py-3 h-auto text-cream focus:ring-0 focus:border-gold text-base [&>span]:text-cream data-[placeholder]:text-[#5a5a55]"
                                        >
                                            <SelectValue placeholder="Select a budget range" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-charcoal-2 border-[#262626] text-cream">
                                            {budgetRanges.map((b) => (
                                                <SelectItem key={b} value={b} className="focus:bg-gold/15 focus:text-gold">{b}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div>
                                <label className="field-label" htmlFor="message">Message</label>
                                <Textarea
                                    id="message"
                                    data-testid="form-message"
                                    value={form.message}
                                    onChange={handleChange("message")}
                                    placeholder="Tell us a little about your space and what you're hoping to create."
                                    rows={4}
                                    className="bg-transparent border-0 border-b border-[#262626] rounded-none px-0 py-3 text-cream placeholder:text-[#5a5a55] focus-visible:ring-0 focus-visible:border-gold text-base resize-none"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="form-submit"
                                className="btn-primary disabled:opacity-60"
                            >
                                {submitting ? "Submitting…" : "Book Free Consultation"} <ArrowRight size={16} />
                            </button>
                        </form>
                    </Reveal>

                    {/* SIDE INFO */}
                    <Reveal className="lg:col-span-5" delay={120}>
                        <div className="bg-charcoal-2 border border-[#1c1c1c] p-10 lg:p-12">
                            <p className="overline mb-6">— Studio</p>
                            <p className="font-display text-3xl md:text-4xl text-cream leading-tight tracking-tight">
                                Jubilee Hills,<br />Hyderabad.
                            </p>
                            <ul className="mt-10 space-y-6">
                                <li className="flex gap-4 items-start">
                                    <MapPin size={18} className="text-gold mt-1 shrink-0" />
                                    <p className="text-cream">Flat No. 301-B, Park View Edifice,<br />Nandagiri Co-Op Housing Society,<br />Jubilee Main Road, Journalist Colony,<br />Jubilee Hills, Hyderabad, Telangana 500033</p>
                                </li>
                                <li className="flex gap-4 items-center">
                                    <Phone size={18} className="text-gold shrink-0" />
                                    <a href="tel:+919249899999" className="text-cream hover:text-gold">+91 92498 99999</a>
                                </li>
                                <li className="flex gap-4 items-center">
                                    <Mail size={18} className="text-gold shrink-0" />
                                    <a href="mailto:contact@midasiseliteinteriors.com" className="text-cream hover:text-gold">contact@midasiseliteinteriors.com</a>
                                </li>
                                <li className="flex gap-4 items-start">
                                    <Clock size={18} className="text-gold mt-1 shrink-0" />
                                    <p className="text-cream">Mon – Sat · 10:00 — 19:00<br />Sun · By appointment</p>
                                </li>
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* MAP */}
            <section data-testid="map-section" className="border-t border-[#262626]">
                <div className="container-pad mx-auto pt-20 md:pt-28 pb-12">
                    <Reveal>
                        <p className="overline mb-6">— Find Us</p>
                        <h2 className="font-display text-3xl md:text-5xl tracking-tight text-cream max-w-3xl">
                            Visit the studio.
                        </h2>
                    </Reveal>
                </div>
                <div className="w-full aspect-[16/8] grayscale-[40%]">
                    <iframe
                        title="Midasis Elite Interiors — Studio Location"
                        src="https://www.google.com/maps?q=Jubilee+Hills,+Hyderabad,+Telangana&output=embed"
                        loading="lazy"
                        className="w-full h-full border-0"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                    />
                </div>
            </section>
        </div>
    );
}
